<script setup lang="ts">
import { IonContent, IonIcon, IonPage, useIonRouter } from '@ionic/vue';
import { useRoute } from 'vue-router';
import { arrowBackCircleSharp, closeCircleOutline, settingsOutline } from 'ionicons/icons';
import { useStore } from '@/stores/main';
import { computed, inject, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import Teamfeeling from '@/components/illustrations/Teamfeeling.vue';
import Goal from '@/components/illustrations/Goal.vue';
import Flag from '@/components/illustrations/Flag.vue';
import Globe from '@/components/illustrations/Globe.vue';
import { featureNotAvailable } from '@/helpers';
import { FmsType } from '@/types';
import { useI18n } from 'vue-i18n';
import { createAnimation } from '@ionic/vue';
// Assets
import logoUrl from '/assets/logo.png';
import angryMonsterUrl from '/assets/monster/arg.png';

const store = useStore();
const router = useIonRouter();
const route = useRoute();
const { t } = useI18n();
const fmsType = inject('fms-type') as FmsType;
const showDenyMessage = ref(false);
const hasPlan = ref(false);
const hasActiveProfile = computed(() => store.profile !== null);
const isPageLoaded = ref(false);
const activeMenuIndex = ref<number | null>(null);
const ionContentRef = ref(null);

// Enhanced function for resetting scroll position
async function resetScrollPosition() {
  // First reset standard document scroll
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  
  // Force a proper reset of Ionic's scroll container
  await nextTick();
  const ionContent = document.querySelector('ion-content');
  if (ionContent) {
    // getScrollElement() returns a Promise in Ionic 7+
    const scrollEl = await (ionContent as any).getScrollElement?.();
    if (scrollEl) {
      // Apply scroll reset
      scrollEl.scrollTop = 0;
      
      // Ensure scroll events are enabled
      (ionContent as any).scrollEvents = true;
      
      // Force a small scroll to "wake up" the scroller
      setTimeout(() => {
        scrollEl.scrollTop = 1;
        setTimeout(() => {
          scrollEl.scrollTop = 0;
        }, 20);
      }, 50);
    }
  }
}

// Reset page state completely
function resetPageState() {
  // Reset animations and active states
  isPageLoaded.value = false;
  activeMenuIndex.value = null;
  
  // Let the reset take effect
  nextTick(() => {
    // Then re-enable animations with a small delay
    setTimeout(() => {
      isPageLoaded.value = true;
    }, 100);
  });
  
  // Reset scroll position
  resetScrollPosition();
}

// Listen for Ionic page lifecycle events
function handleIonViewWillEnter() {
  resetPageState();
}

// Animation control
onMounted(() => {
  // Add proper Ionic page lifecycle event listeners
  document.addEventListener('ionViewWillEnter', handleIonViewWillEnter);
  document.addEventListener('ionViewDidEnter', resetScrollPosition);
  
  // Reset everything on initial mount
  resetPageState();
  
  // Listen for route changes
  window.addEventListener('popstate', resetPageState);
});

// Clean up event listeners when component unmounts
onBeforeUnmount(() => {
  window.removeEventListener('popstate', resetPageState);
  document.removeEventListener('ionViewWillEnter', handleIonViewWillEnter);
  document.removeEventListener('ionViewDidEnter', resetScrollPosition);
});

// Watch for navigation events
watch(() => router.canGoBack(), (canGoBack) => {
  // If we detect navigation, ensure page state is fresh
  resetPageState();
});

// Enhanced back button navigation function
function goToHomePage() {
  try {
    // Reset animation states
    activeMenuIndex.value = null;
    isPageLoaded.value = false;

    // Delay navigation to allow animations to complete
    setTimeout(() => {
      // Check if navigation is still valid
      if (router.canGoBack() && route.path !== '/home') {
        router.navigate('/home', 'back', 'replace');
      } else {
        // If already on home or no back navigation, replace the route
        router.replace('/home');
      }

      // Reset scroll position and page state after navigation
      setTimeout(() => {
        resetPageState();
      }, 300); // Match animation duration
    }, 300); // Allow animation to complete
  } catch (error) {
    console.error('Navigation to home page failed:', error);
    // Optionally show a user-friendly error message
    featureNotAvailable();
  }
}

function checkIfDisabled(event: Event) {
  const target = event.target as HTMLElement;
  if (target.dataset.disabled === 'true') {
    showDenyMessage.value = true;
    return false;
  }
  return true;
}

// Consistent navigation helper that properly manages animations
function navigateToPage(
  event: Event,
  path: string,
  method: 'navigate' | 'replace' = 'replace',
  menuIndex: number | null = null
) {
  if (
    checkIfDisabled(event) &&
    hasActiveProfile.value &&
    !showDenyMessage.value
  ) {
    // Show animation
    activeMenuIndex.value = menuIndex;
    
    // Delay navigation to allow animation
    setTimeout(() => {
      // Use consistent navigation method and force refresh
      if (method === 'navigate') {
        // Always use replace if we're already on this page (to force reload)
        if (route.path.includes(path)) {
          router.replace(path);
        } else {
          router.navigate(path);
        }
      } else {
        router.replace(path);
      }
      
      // Reset animations after navigation 
      setTimeout(() => {
        activeMenuIndex.value = null;
        resetScrollPosition();
      }, 300);
    }, 300);
  }
}

// Use consistent approach for all navigation functions
function goToFirstQuestionPage(event: Event) {
  // For first step, always use replace to force a fresh component state
  navigateToPage(event, '/questions/1', 'replace', 0);
}

function goToSecondQuestionPage(event: Event) {
  navigateToPage(event, '/questions/2', 'replace', 1);
}

function goToGoalsPage(event: Event) {
  navigateToPage(event, '/goals', 'replace', 2);
}

function goToPlanPage(event: Event) {
  if (
    checkIfDisabled(event) &&
    hasActiveProfile.value &&
    !showDenyMessage.value
  ) {
    activeMenuIndex.value = 3;
    setTimeout(() => {
      const path = hasPlan.value ? '/plan/results' : '/plan/prelude';
      router.replace(path);
      
      // Reset animation state after navigation
      setTimeout(() => {
        activeMenuIndex.value = null;
      }, 300);
    }, 300);
  }
}

function checkAndToast(event: Event) {
  if (checkIfDisabled(event)) {
    featureNotAvailable();
  }
}

function closeDenyMessage() {
  showDenyMessage.value = false;
}

function settingsClick() {
  featureNotAvailable();
}
</script>

<template>
  <ion-page>
    <ion-content 
      class="content-container" 
      ref="ionContentRef"
      :scroll-events="true"
      @ionScrollStart="() => {}"
    >
      <!-- Header Section -->
      <header class="app-header">
        <div class="header-content">
          <button class="back-button" @click="goToHomePage">
            <ion-icon :icon="arrowBackCircleSharp"></ion-icon>
          </button>
          
          <div class="logo-container">
            <img :src="logoUrl" :alt="$t('HomePage.logoAlt')" class="logo" />
          </div>
          
          <button class="settings-button" @click="settingsClick">
            <ion-icon :icon="settingsOutline"></ion-icon>
          </button>
        </div>
      </header>

      <!-- Main Content Section -->
      <main class="main-container" :class="{ 'fade-in': isPageLoaded }">
        <div class="text-container">
          <h1 class="main-title" v-html="$t('HomePage.personalPlanTitle')"></h1>
          
          <div class="description-text">
            <p>
              {{ $t('HomePage.bwellDescription') }}
            </p>
            
            <p class="privacy-note">
              {{ $t('HomePage.privacyNotice') }}
            </p>
            
            <p class="instruction-text" v-if="fmsType === FmsType.Work" v-html="$t('HomePage.clickToStartWork')">
            </p>
            <p class="instruction-text" v-else v-html="$t('HomePage.clickToStartSchool')">
            </p>
          </div>
        </div>

        <!-- Menu Grid -->
        <div class="menu-grid">
          <div class="menu-item" 
               :class="{ 'active': activeMenuIndex === 0, 'loaded': isPageLoaded }"
               style="--delay: 0.1s">
            <b-start-button
              color="yellow"
              :number="1"
              class="menu-button"
              :class="{ 'pulse': !store.hasAnyValues() }"
              @click="goToFirstQuestionPage"
            >
              <template #icon>
                <Teamfeeling class="icon-container" />
              </template>
              <template v-if="fmsType === FmsType.Work">{{ $t('HomePage.myWork') }}</template>
              <template v-else>{{ $t('HomePage.mySchool') }}</template>
            </b-start-button>
          </div>

          <div class="menu-item" 
               :class="{ 'active': activeMenuIndex === 1, 'loaded': isPageLoaded }"
               style="--delay: 0.2s">
            <b-start-button
              color="green"
              :number="2"
              class="menu-button"
              @click="goToSecondQuestionPage"
            >
              <template #icon>
                <Globe class="icon-container" />
              </template>
              {{ $t('HomePage.myLifestyle') }}
            </b-start-button>
          </div>

          <div class="menu-item" 
            :class="{ 'active': activeMenuIndex === 2, 'loaded': isPageLoaded }"
            style="--delay: 0.3s">
            <b-start-button
              color="blue"
              :number="3"
              :data-disabled="!store.hasAnyValues()"
              class="menu-button"
              @click="goToGoalsPage"
            >
              <template #icon>
                <Flag class="icon-container" />
              </template>
              {{ $t('HomePage.myGoals') }}
            </b-start-button>
          </div>

          <div class="menu-item" 
               :class="{ 'active': activeMenuIndex === 3, 'loaded': isPageLoaded }"
               style="--delay: 0.4s">
            <b-start-button
              color="yellow"
              :number="4"
              :disabled="!store.hasAnyValues()"
              class="menu-button"
              @click="goToPlanPage"
            >
              <template #icon>
                <Goal class="icon-container" />
              </template>
              {{ $t('HomePage.myLifestylePlan') }}
            </b-start-button>
          </div>
        </div>
      </main>

      <!-- Deny Message Modal -->
      <Transition name="modal">
        <div class="deny-overlay" v-if="showDenyMessage" v-on-click-outside="closeDenyMessage">
          <div class="deny-modal">
            <button class="close-button" @click="closeDenyMessage">
              <ion-icon :icon="closeCircleOutline"></ion-icon>
            </button>
            
            <speech-bubble color="purple" class="deny-bubble">
              <div class="deny-text" v-html="$t('HomePage.denyMessage')">
              </div>
            </speech-bubble>
            
            <img :src="angryMonsterUrl" class="angry-monster" :alt="$t('HomePage.angryMonsterAlt')" />
          </div>
        </div>
      </Transition>
      
      <!-- Decorative Elements -->
      <div class="decorative-shape shape-1"></div>
      <div class="decorative-shape shape-2"></div>
      <div class="decorative-shape shape-3"></div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
/* Base Styles */
ion-content {
  --background: linear-gradient(150deg, #fffafd 0%, #fff5fa 50%, #f7f0ff 100%);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.content-container {
  position: relative;
  min-height: 100%;
  overflow-x: hidden;
}

/* Header Styles */
.app-header {
  position: relative;
  padding: 1rem 1.5rem;
  z-index: 10;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
}

.logo-container {
  flex-grow: 1;
  display: flex;
  justify-content: center;
}

.logo {
  height: auto;
  object-fit: contain;
  width: 6rem;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

.back-button, .settings-button {
  background: transparent;
  border: none;
  color: #7b1fa2;
  cursor: pointer;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.back-button:hover, .settings-button:hover {
  background-color: rgba(123, 31, 162, 0.1);
  transform: scale(1.1);
}

.back-button ion-icon, .settings-button ion-icon {
  font-size: 1.75rem;
}

/* Main Content Styles */
.main-container {
  padding: 1rem 1.5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.main-container.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.text-container {
  text-align: center;
  margin-bottom: 2rem;
}

.main-title {
  font-size: 1.75rem;
  font-weight: 300;
  color: #171717;
  margin-bottom: 1.5rem;
  line-height: 1.3;
}

.highlight {
  color: #7b1fa2;
  font-weight: 500;
}

.description-text {
  max-width: 36rem;
  margin: 0 auto;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1.6;
}

.description-text p {
  margin-bottom: 1rem;
}

.privacy-note {
  font-weight: 500;
  color: #171717;
}

.instruction-text {
  font-size: 1.05rem;
  color: #7b1fa2;
}

/* Menu Grid Styles */
.menu-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  max-width: 42rem;
  margin: 0 auto;
}

.menu-item {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease, scale 0.3s ease;
  transition-delay: var(--delay);
}

.menu-item.loaded {
  opacity: 1;
  transform: translateY(0);
}

.menu-item.active {
  transform: scale(0.95);
}

.menu-button {
  height: 100%;
  width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.menu-button:not([data-disabled="true"]):hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.icon-container {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

/* Pulse Animation for First Menu Item */
.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
  }
}

/* Deny Modal Styles */
.deny-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(249, 253, 242, 0.96);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.deny-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90%;
}

.close-button {
  position: absolute;
  top: -20px;
  right: -20px;
  background: transparent;
  border: none;
  color: #171717;
  font-size: 1.75rem;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.2s ease;
}

.close-button:hover {
  transform: scale(1.1);
}

.deny-bubble {
  width: 280px;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.deny-text {
  font-size: 1.1rem;
  line-height: 1.4;
  padding: 0.5rem;
}

.angry-monster {
  width: 40%;
  max-width: 180px;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2));
  animation: bounce 0.8s ease;
}

/* Decorative Shapes */
.decorative-shape {
  position: absolute;
  border-radius: 50%;
  z-index: -1;
  opacity: 0.5;
  filter: blur(50px);
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(123, 31, 162, 0.1), rgba(123, 31, 162, 0.05));
  top: -100px;
  right: -100px;
}

.shape-2 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
  bottom: -50px;
  left: -50px;
}

.shape-3 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05));
  top: 40%;
  left: 25%;
}

/* Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Responsive Styles */
@media (min-width: 640px) {
  .main-title {
    font-size: 2rem;
  }
  
  .logo {
    width: 7rem;
  }
  
  .deny-bubble {
    width: 320px;
  }
  
  .deny-text {
    font-size: 1.25rem;
  }
}

@media (min-width: 768px) {
  .app-header {
    padding: 1.5rem 2rem;
  }
  
  .main-container {
    padding: 1rem 2rem 3rem;
  }
  
  .main-title {
    font-size: 2.25rem;
  }
  
  .logo {
    width: 8rem;
  }
  
  .menu-grid {
    gap: 1.5rem;
  }
  
  .description-text {
    font-size: 1.1rem;
  }
}

@media (min-width: 1024px) {
  .main-title {
    font-size: 2rem;
  }
  
  .description-text {
    font-size: 1rem;
  }
  
  .menu-grid {
    gap: 1.25rem;
    max-width: 38rem;
  }
  
  .logo {
    width: 7rem;
  }
}

@media (min-width: 1280px) {
  .main-title {
    font-size: 2.25rem;
  }
  
  .menu-grid {
    gap: 1.5rem;
    max-width: 42rem;
  }
}

/* Landscape Mode Adjustments */
@media (orientation: landscape) and (max-height: 600px) {
  .app-header {
    padding: 0.75rem 1.5rem;
  }
  
  .main-container {
    padding: 0 1.5rem 1.5rem;
  }
  
  .main-title {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
  
  .description-text {
    font-size: 0.9rem;
  }
  
  .text-container {
    margin-bottom: 1rem;
  }
  
  .menu-grid {
    gap: 1rem;
  }
  
  .logo {
    width: 5rem;
  }
}
</style>